import { useState, useEffect } from 'react';
import { Navigate, useSearchParams, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Button,
  Input,
  Label,
  Alert,
} from 'reactstrap';
import { FileText, CreditCard, GraduationCap } from 'lucide-react';
import Footer from '../../components/usuarios/Footer';
import Loader from '../../layouts/loader/Loader';
import redsysApiService from '../../services/http/redsysApiService';
import { submitFormularioRedsys } from '../../helpers/redsysForm';
import logoCedesca from '../../assets/images/logos/logo-cedesca.svg';
import logoCntec from '../../assets/images/logos/logo-cntec-0.svg';

const SEDES = {
  cedesca: {
    nombre: 'CEDESCA',
    razonSocial: "CENTRE D'ESTUDIS CATALUNYA",
    logo: logoCedesca,
    headquarter: 'CEDESCA',
    terminosPdf: '/docs/terminosCedesca.pdf',
    terminosId: 'tyc-cedesca-v1',
  },
  cntec: {
    nombre: 'CNTEC',
    razonSocial: 'CNTEC EDUCATION',
    logo: logoCntec,
    headquarter: 'CNTEC',
    terminosPdf: '/docs/terminosCntec.pdf',
    terminosId: 'tyc-cntec-v1',
  },
};

const agruparPorModulo = (actividades = []) => {
  const grupos = new Map();
  actividades.forEach((act) => {
    const key = act.codigoOficial || act.modulo || act.codigo;
    if (!grupos.has(key)) {
      grupos.set(key, { codigoOficial: act.codigoOficial, modulo: act.modulo, actividades: [] });
    }
    grupos.get(key).actividades.push(act);
  });
  return Array.from(grupos.values());
};

const extraerErrorMensaje = (err) => {
  const status = err?.response?.status;
  const body = err?.response?.data;
  if (body?.error) return { status, mensaje: body.error };
  if (status === 404) return { status, mensaje: 'Pago no encontrado.' };
  if (status === 409) return { status, mensaje: 'Este pago ya no está pendiente.' };
  return { status, mensaje: 'No se pudo cargar la información del pago.' };
};

const Pagar = ({ sede, defaultUid }) => {
  const sedeInfo = SEDES[sede];
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const uid = searchParams.get('uid');

  const [aceptadoTerminos, setAceptadoTerminos] = useState(false);

  const [infoPago, setInfoPago] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!uid) return undefined;
    let cancelado = false;

    setCargando(true);
    setError(null);

    redsysApiService
      .cargarInfoPago({ uid, headquarter: sedeInfo.headquarter })
      .then((data) => {
        if (!cancelado) setInfoPago(data);
      })
      .catch((err) => {
        if (!cancelado) setError(extraerErrorMensaje(err));
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [uid, sedeInfo.headquarter]);

  if (!uid) {
    return <Navigate to={`${location.pathname}?uid=${defaultUid}`} replace />;
  }

  const puedePagar = aceptadoTerminos && infoPago && !enviando;

  const handlePagar = async () => {
    setEnviando(true);
    setError(null);
    try {
      const respuesta = await redsysApiService.iniciarPago({
        uid,
        headquarter: sedeInfo.headquarter,
        acuerdosAceptados: [sedeInfo.terminosId],
      });
      submitFormularioRedsys(respuesta);
    } catch (err) {
      setError(extraerErrorMensaje(err));
      setEnviando(false);
    }
  };

  const modulos = infoPago ? agruparPorModulo(infoPago.actividades) : [];
  const importeRecibo = infoPago?.reciboActual?.importe ?? infoPago?.precioTotal;

  return (
    <>
      <main className="bg-light min-vh-100 py-4">
        <Container>
          <Row className="justify-content-center mb-4">
            <Col xs="12" className="text-center">
              <img
                src={sedeInfo.logo}
                alt={sedeInfo.nombre}
                className="d-block mx-auto"
                style={{ height: '120px', maxWidth: '100%', width: 'auto' }}
              />
              <h2 className="mt-3 mb-1">Pago de matrícula y actividades</h2>
              <p className="text-muted">
                Revise el detalle, acepte los términos y proceda al pago seguro con Cyberpac.
              </p>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col lg="8">
              {cargando && (
                <Card className="shadow-sm mb-4">
                  <CardBody className="p-4">
                    <Loader />
                  </CardBody>
                </Card>
              )}

              {!cargando && error && (
                <Alert color="danger" className="mb-4">
                  {error.mensaje}
                </Alert>
              )}

              {!cargando && !error && infoPago && (
                <>
                  <Card className="shadow-sm mb-4">
                    <CardTitle tag="h5" className="border-bottom px-4 py-3 mb-0">
                      Itinerario académico
                    </CardTitle>
                    <CardBody className="p-4">
                      <div className="mb-3 text-muted">
                        <strong>Alumno:</strong> {infoPago.nombreAlumno}
                        <br />
                        <strong>Email:</strong> {infoPago.emailAlumno}
                        {infoPago.numMatricula && (
                          <>
                            <br />
                            <strong>Nº matrícula:</strong> {infoPago.numMatricula}
                          </>
                        )}
                      </div>
                      <p className="mb-4">
                        Se ha recibido la solicitud de matrícula para{' '}
                        <strong>{infoPago.curso}</strong> con las siguientes asignaturas:
                      </p>

                      {modulos.map((modulo) => (
                        <div key={modulo.codigoOficial || modulo.modulo} className="mb-3">
                          <div className="d-flex align-items-start gap-3">
                            <GraduationCap size={22} className="text-warning flex-shrink-0 mt-1" />
                            <div className="flex-grow-1">
                              <div className="fw-bold">{modulo.modulo}</div>
                              <ul className="list-unstyled ms-3 mt-2 mb-0">
                                {modulo.actividades.map((actividad) => (
                                  <li key={actividad.codigo} className="text-muted small py-1">
                                    {actividad.descripcion}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      ))}
                    </CardBody>
                  </Card>

                  {infoPago.cuotas?.length > 0 && (
                    <Card className="shadow-sm mb-4">
                      <CardTitle tag="h5" className="border-bottom px-4 py-3 mb-0">
                        Plan de pago
                      </CardTitle>
                      <CardBody className="p-4">
                        <Table responsive borderless className="mb-0">
                          <thead className="border-bottom">
                            <tr>
                              <th>Concepto</th>
                              <th>Vencimiento</th>
                              <th className="text-end">Importe</th>
                            </tr>
                          </thead>
                          <tbody>
                            {infoPago.cuotas.map((cuota, idx) => {
                              const esActual = cuota.uid === uid;
                              return (
                                <tr
                                  key={cuota.numRecibo}
                                  className={esActual ? 'fw-semibold' : 'text-muted'}
                                >
                                  <td>
                                    {esActual && idx === 0 && (
                                      <span className="badge bg-primary me-2">A pagar ahora</span>
                                    )}
                                    {cuota.concepto}
                                  </td>
                                  <td>{cuota.fechaVto}</td>
                                  <td className="text-end">{Number(cuota.importe).toFixed(2)} €</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </CardBody>
                    </Card>
                  )}

                  <Card className="shadow-sm mb-4">
                    <CardTitle tag="h5" className="border-bottom px-4 py-3 mb-0">
                      Términos y condiciones
                    </CardTitle>
                    <CardBody className="p-4">
                      <p className="text-muted mb-3">
                        Antes de continuar, lea el documento de términos y condiciones de
                        contratación de <strong>{sedeInfo.nombre}</strong>:
                      </p>

                      <Button
                        color="outline-primary"
                        block
                        href={sedeInfo.terminosPdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-100 py-3 mb-3 d-flex align-items-center justify-content-center gap-2"
                      >
                        <FileText size={18} />
                        Ver términos y condiciones de contratación ({sedeInfo.nombre})
                      </Button>

                      <div className="form-check">
                        <Input
                          id="check-terminos"
                          type="checkbox"
                          className="form-check-input"
                          checked={aceptadoTerminos}
                          onChange={(e) => setAceptadoTerminos(e.target.checked)}
                        />
                        <Label for="check-terminos" className="form-check-label">
                          He leído y acepto los{' '}
                          <strong>términos y condiciones de contratación</strong> de{' '}
                          {sedeInfo.nombre}.
                        </Label>
                      </div>

                      {!aceptadoTerminos && (
                        <Alert color="warning" className="mt-3 mb-0 py-2 small">
                          Debe leer y aceptar los términos y condiciones para poder continuar con el
                          pago.
                        </Alert>
                      )}
                    </CardBody>
                  </Card>

                  <div className="text-center mb-5">
                    <Button
                      color="primary"
                      size="lg"
                      disabled={!puedePagar}
                      onClick={handlePagar}
                      className="px-5 py-3 d-inline-flex align-items-center gap-2"
                    >
                      <CreditCard size={20} />
                      {enviando
                        ? 'Redirigiendo a Cyberpac…'
                        : `Pagar ${Number(importeRecibo).toFixed(2)} €`}
                    </Button>
                    <p className="text-muted small mt-2 mb-0">
                      Será redirigido al entorno seguro de Cyberpac para completar la operación.
                    </p>
                    <p className="text-muted small mt-2 mb-0">
                      Si confirmas el pago, le permitirás a <strong>{sedeInfo.razonSocial}</strong>{' '}
                      que te cargue este pago y futuros pagos conforme a las condiciones
                      estipuladas.
                    </p>
                  </div>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </main>

      <Footer />
    </>
  );
};

Pagar.propTypes = {
  sede: PropTypes.oneOf(Object.keys(SEDES)).isRequired,
  defaultUid: PropTypes.string.isRequired,
};

export default Pagar;
